import React from 'react';
import { Map, useMap } from '@vis.gl/react-google-maps';

import type { RunMapStop, ClickedRunStop } from '../runTypes';
import { useRunOverlays } from '../hooks/useRunOverlays';
import RunStopInfoPopup from './RunStopInfoPopup';
import { useMapPreferences } from '@components/Maps';

// TYPES --------------------------------------------------------------------- //

interface RunMapViewProps {
  mapId: string;
  stops: RunMapStop[];
  /** Encoded Google polylines for each leg (index i = leg from stop i to stop i+1) */
  legPolylines?: (string | null)[];
  showGeofences?: boolean;
  defaultBounds?: google.maps.LatLngBoundsLiteral;
  defaultCenter?: google.maps.LatLngLiteral;
  defaultZoom?: number;
  /** Bounds to fly to (set externally, e.g. when a stop is selected) */
  flyToBounds?: google.maps.LatLngBoundsLiteral | null;
  /** Index of selected stop for highlighting overlays */
  selectedStopIndex?: number | null;
  children?: React.ReactNode;
}

// COMPONENT ----------------------------------------------------------------- //

const RunMapView: React.FC<RunMapViewProps> = ({
  mapId,
  stops,
  legPolylines,
  showGeofences = true,
  defaultBounds,
  defaultCenter = { lat: 33.749, lng: -84.388 },
  defaultZoom = 12,
  flyToBounds,
  selectedStopIndex,
  children,
}) => {
  const map = useMap(mapId);
  const { mapStyles } = useMapPreferences();

  const [clickedStop, setClickedStop] = React.useState<ClickedRunStop | null>(null);

  const handleClickStop = React.useCallback((stop: ClickedRunStop | null) => {
    setClickedStop(stop);
  }, []);

  // Close popup when clicking the map background
  React.useEffect(() => {
    if (!map) return;
    const listener = map.addListener('click', () => setClickedStop(null));
    return () => google.maps.event.removeListener(listener);
  }, [map]);

  // Frame the map to the active bounds. `defaultBounds` usually arrives
  // *after* the map is ready (query data lands post-mount) so it must
  // retrigger this effect — but only when its values actually change, since
  // live queries and polls rebuild an identical bounds object every tick and
  // refitting would yank the map away from the user mid-pan. The center
  // fallback needs the same treatment: <Map>'s defaultCenter is uncontrolled,
  // so a late-arriving center (e.g. the dealership location) has to be
  // applied imperatively.
  const framedKeyRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!map) return;
    const bounds = flyToBounds ?? defaultBounds ?? null;
    const key = bounds
      ? `b:${bounds.north},${bounds.south},${bounds.east},${bounds.west}`
      : `c:${defaultCenter.lat},${defaultCenter.lng}`;
    if (key === framedKeyRef.current) return;
    framedKeyRef.current = key;
    if (bounds) {
      const pad = flyToBounds ? 60 : 40;
      map.fitBounds(bounds, { top: pad, bottom: pad, left: pad, right: pad });
    } else {
      map.setCenter(defaultCenter);
      map.setZoom(defaultZoom);
    }
  }, [map, flyToBounds, defaultBounds, defaultCenter, defaultZoom]);

  // Auto-switch to satellite at high zoom (same as moves map)
  React.useEffect(() => {
    if (!map) return;
    const SATELLITE_ZOOM = 19;
    const listener = map.addListener('zoom_changed', () => {
      const zoom = map.getZoom() ?? 10;
      if (zoom >= SATELLITE_ZOOM && map.getMapTypeId() !== 'hybrid') {
        map.setMapTypeId('hybrid');
      } else if (zoom < SATELLITE_ZOOM && map.getMapTypeId() === 'hybrid') {
        map.setMapTypeId('roadmap');
      }
    });
    return () => google.maps.event.removeListener(listener);
  }, [map]);

  // Build overlays
  useRunOverlays({
    map,
    stops,
    legPolylines,
    showGeofences,
    selectedStopIndex,
    onClickStop: handleClickStop,
  });

  return (
    <>
      <Map
        id={mapId}
        defaultCenter={defaultCenter}
        defaultZoom={defaultZoom}
        defaultBounds={defaultBounds}
        gestureHandling='greedy'
        disableDefaultUI={false}
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={false}
        styles={mapStyles}
        style={{ width: '100%', height: '100%' }}
      >
        {children}
      </Map>

      {clickedStop ? (
        <RunStopInfoPopup
          clickedStop={clickedStop}
          onClose={() => setClickedStop(null)}
        />
      ) : null}
    </>
  );
};

export default RunMapView;
